import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '../contexts/AuthContext';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useAnalysisGlobal } from '../contexts/AnalysisContext';
import { metricsAPI, bidAgentsAPI } from '../services/api';
import {
  Bookmark, Search, ArrowRight, Sparkles, Clock, FileText,
  BarChart3, ChevronRight
} from 'lucide-react';
import ElasticAgentReport from '../components/ElasticAgentReport';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { bookmarks, bookmarkCount } = useBookmarks();
  const { analyses, anyRunning, runningAnalyses } = useAnalysisGlobal();

  const [stats, setStats] = useState({
    savedTenders: 0, totalTenders: 0, activeTenders: 0,
    new48h: 0, closing72h: 0,
  });
  const [metrics, setMetrics] = useState(null);
  const [pipelineReport, setPipelineReport] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const metricsResponse = await metricsAPI.getMetrics().catch(() => null);
      setMetrics(metricsResponse);
      setStats({
        savedTenders: bookmarkCount,
        totalTenders: metricsResponse?.tender_counts?.total || 0,
        activeTenders: metricsResponse?.tender_counts?.active || 0,
        new48h: metricsResponse?.tender_counts?.new_48h || 0,
        closing72h: metricsResponse?.tender_counts?.closing_72h || 0,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [bookmarkCount]);

  const fetchPipelineReport = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const report = await bidAgentsAPI.getPipelineReport();
      setPipelineReport(report);
    } catch (err) {
      console.error('Error fetching pipeline report:', err);
    } finally {
      setPipelineLoading(false);
    }
  }, []);

  const refreshPipelineReport = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const report = await bidAgentsAPI.refreshPipelineReport();
      setPipelineReport(report);
    } catch (err) {
      console.error('Error refreshing pipeline report:', err);
    } finally {
      setPipelineLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchPipelineReport();
  }, [fetchDashboardData, fetchPipelineReport]);

  // Helpers
  const truncate = (str, len = 60) => {
    if (!str) return '';
    return str.length > len ? str.slice(0, len) + '...' : str;
  };

  // Recent activity — last 5 analyzed tenders (from analyses context)
  const recentAnalyzed = Object.entries(analyses)
    .filter(([, a]) => a.status === 'complete')
    .sort(([, a], [, b]) => (b.startedAt || 0) - (a.startedAt || 0))
    .slice(0, 5);

  // Navigation handlers — params must match what Dashboard reads
  const handleNewTendersClick = () => {
    const fromDate = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString().split('T')[0];
    navigate(`/dashboard?from_date=${fromDate}&sort_by=date`);
  };
  const handleClosingSoonClick = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    const closingTo = new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString().split('T')[0];
    navigate(`/dashboard?closing_from=${todayStr}&closing_to=${closingTo}&sort_by=date`);
  };

  return (
    <div className="home-container">
      {/* Welcome Header */}
      <div className="home-header">
        <div className="welcome-section">
          <div className="welcome-logo">
            <img src="/AIquire.png" alt="AIquire" />
          </div>
          <div className="welcome-content">
            <h1 className="welcome-title">Welcome back, {user?.name || 'User'}</h1>
            <p className="welcome-subtitle">Here's what's happening with Canadian government tenders today</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card stat-card-clickable" onClick={handleNewTendersClick}>
          <div className="stat-icon stat-icon-new"><Sparkles size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">New (48h)</div>
            <div className="stat-value">{stats.new48h?.toLocaleString() || 0}</div>
            <div className="stat-change">Posted recently</div>
          </div>
          <div className="stat-arrow"><ArrowRight size={16} /></div>
        </div>
        <div className="stat-card stat-card-clickable" onClick={handleClosingSoonClick}>
          <div className="stat-icon stat-icon-warning"><Clock size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">Closing Soon</div>
            <div className="stat-value">{stats.closing72h?.toLocaleString() || 0}</div>
            <div className="stat-change">Within 72 hours</div>
          </div>
          <div className="stat-arrow"><ArrowRight size={16} /></div>
        </div>
        <div className="stat-card stat-card-clickable" onClick={() => navigate('/my-tenders')}>
          <div className="stat-icon stat-icon-success"><Bookmark size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">Saved</div>
            <div className="stat-value">{bookmarkCount}</div>
            <div className="stat-change">Ready to review</div>
          </div>
          <div className="stat-arrow"><ArrowRight size={16} /></div>
        </div>
        <div className="stat-card stat-card-clickable" onClick={() => navigate('/dashboard')}>
          <div className="stat-icon stat-icon-primary"><FileText size={24} /></div>
          <div className="stat-content">
            <div className="stat-label">Total Active</div>
            <div className="stat-value">{stats.activeTenders?.toLocaleString() || 0}</div>
            <div className="stat-change">Open for bids</div>
          </div>
          <div className="stat-arrow"><ArrowRight size={16} /></div>
        </div>
      </div>

      {/* Agentic Search + Pipeline Briefing (tabbed) */}
      <div className="home-agentic-search">
        <ElasticAgentReport
          pipelineReport={pipelineReport}
          pipelineLoading={pipelineLoading}
          onRefreshPipeline={refreshPipelineReport}
        />
      </div>

      {/* Two column: Quick Actions + Recent Activity */}
      <div className="home-two-col">
        {/* Quick Actions */}
        <div className="quick-actions">
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid actions-grid-2x2">
            <button className="action-card" onClick={() => navigate('/dashboard')}>
              <div className="action-icon"><Search size={20} /></div>
              <div className="action-content"><h3>Search Tenders</h3><p>Find opportunities</p></div>
            </button>
            <button className="action-card" onClick={() => navigate('/my-tenders')}>
              <div className="action-icon action-icon-green"><Bookmark size={20} /></div>
              <div className="action-content"><h3>Saved Tenders</h3><p>{bookmarkCount} saved</p></div>
            </button>
            <button className="action-card" onClick={() => navigate('/my-tenders')}>
              <div className="action-icon action-icon-purple"><FileText size={20} /></div>
              <div className="action-content"><h3>Responses</h3><p>View analyses</p></div>
            </button>
            <button className="action-card" onClick={() => navigate('/analytics')}>
              <div className="action-icon action-icon-teal"><BarChart3 size={20} /></div>
              <div className="action-content"><h3>Analytics</h3><p>Pipeline metrics</p></div>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="recent-section">
          <div className="section-header-row">
            <h2 className="section-title">Recent Activity</h2>
            {recentAnalyzed.length > 0 && (
              <button className="view-all-link" onClick={() => navigate('/my-tenders')}>
                View All <ChevronRight size={14} />
              </button>
            )}
          </div>

          {/* Running analyses */}
          {anyRunning && (
            <div className="activity-running">
              {runningAnalyses.map(tid => {
                const a = analyses[tid];
                const completed = a?.completedPhases?.length || 0;
                const bm = bookmarks.find(b => b.id === tid || b.tender_id === tid);
                return (
                  <div key={tid} className="activity-card activity-card-running" onClick={() => navigate(`/tender/${tid}`)}>
                    <div className="activity-card-top">
                      <span className="status-badge status-badge--analyzing">Analyzing...</span>
                      <span className="activity-phase">Phase {completed}/5</span>
                    </div>
                    <div className="activity-card-title">{truncate(bm?.title || tid, 50)}</div>
                    <div className="activity-progress-bar">
                      <div className="activity-progress-fill" style={{ width: `${(completed / 5) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Completed analyses */}
          {recentAnalyzed.length > 0 ? (
            <div className="activity-list">
              {recentAnalyzed.map(([tid, a]) => {
                const rec = (a.phases?.phase4?.recommendation || a.phases?.phase3?.recommendation || '').toUpperCase();
                const bm = bookmarks.find(b => b.id === tid || b.tender_id === tid);
                const recCls = rec === 'BID' ? 'rec-badge-bid' : rec === 'CONSIDER' ? 'rec-badge-consider' : rec === 'PASS' ? 'rec-badge-pass' : 'rec-badge-neutral';
                return (
                  <div key={tid} className="activity-card" onClick={() => navigate(`/tender/${tid}`)}>
                    <div className="activity-card-top">
                      <span className={`rec-badge ${recCls}`}>{rec || 'DONE'}</span>
                    </div>
                    <div className="activity-card-title">{truncate(bm?.title || tid, 50)}</div>
                  </div>
                );
              })}
            </div>
          ) : !anyRunning ? (
            <div className="empty-state-card-home">
              <div className="empty-icon-wrapper-home"><BarChart3 size={28} strokeWidth={1.5} /></div>
              <div className="empty-state-text">
                <h3>No recent analyses</h3>
                <p>Run a bid analysis from any tender detail page to see results here.</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Pipeline Coverage */}
      {metrics && metrics.field_coverage && (
        <div className="metrics-section">
          <h2 className="section-title">Pipeline Coverage</h2>
          <div className="coverage-grid">
            <div className="coverage-item">
              <div className="coverage-label">Title</div>
              <div className="coverage-bar">
                <div className="coverage-fill" style={{ width: `${metrics.field_coverage.title || 0}%` }} />
              </div>
              <div className="coverage-value">{(metrics.field_coverage.title || 0).toFixed(1)}%</div>
            </div>
            <div className="coverage-item">
              <div className="coverage-label">Description</div>
              <div className="coverage-bar">
                <div className="coverage-fill" style={{ width: `${metrics.field_coverage.description || 0}%` }} />
              </div>
              <div className="coverage-value">{(metrics.field_coverage.description || 0).toFixed(1)}%</div>
            </div>
            <div className="coverage-item">
              <div className="coverage-label">Organization</div>
              <div className="coverage-bar">
                <div className="coverage-fill" style={{ width: `${metrics.field_coverage.organization || 0}%` }} />
              </div>
              <div className="coverage-value">{(metrics.field_coverage.organization || 0).toFixed(1)}%</div>
            </div>
            <div className="coverage-item">
              <div className="coverage-label">Closing Date</div>
              <div className="coverage-bar">
                <div className="coverage-fill" style={{ width: `${metrics.field_coverage.closing_date || 0}%` }} />
              </div>
              <div className="coverage-value">{(metrics.field_coverage.closing_date || 0).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
